import type { MessageDescriptor } from '@lingui/core'
import type { Database, Enums, Json, Tables, TablesInsert } from './db_types'

export type Organization = Database['public']['Tables']['organizations']['Row']
export type Team = Database['public']['Tables']['teams']['Row']
export type TeamPlan = Database['public']['Tables']['teams_plans']['Row']
export type TeamPlanInsert = Database['public']['Tables']['teams_plans']['Insert']
export type VirtualTableKind = 'kpi' | 'custom_record'
export type VirtualTableScope = {
  kind: VirtualTableKind
  tableId: number
}
export type TeamWithJoins = Tables<'teams'> & {
  profiles: Tables<'profiles'>[] | null
  plans: Tables<'plans'>[] | null
  tags: Tables<'tags'>[] | null
}
export type Plan = Database['public']['Tables']['plans']['Row']
export type PlanInsert = Database['public']['Tables']['plans']['Insert']
export type Profile = Database['public']['Tables']['profiles']['Row']
export type ProfileWithCompensationLetters = Profile & {
  teams_profiles: Tables<'teams_profiles'>[] | null
  compensation_letters: Tables<'compensation_letters'>[] | null
}

export type ProfileWithOrgContextJoins = Pick<
  Profile,
  | 'created_at'
  | 'email'
  | 'id'
  | 'img_url'
  | 'name'
  | 'name_lower_case'
  | 'name_normalized'
  | 'onboarded'
  | 'tax_id_number'
  | 'updated_at'
> & {
  organizations_profiles:
    | Pick<Tables<'organizations_profiles'>, 'organization_id' | 'role'>[]
    | null
  profile_org_start_date: Tables<'profile_org_start_date'>[] | null
  teams_profiles:
    | (Pick<
        Tables<'teams_profiles'>,
        | 'created_at'
        | 'full_view_access'
        | 'organization_id'
        | 'profile_id'
        | 'role'
        | 'team_id'
        | 'updated_at'
      > & {
        teams: Pick<
          Tables<'teams'>,
          | 'archived_at'
          | 'created_at'
          | 'currency'
          | 'description'
          | 'id'
          | 'name'
          | 'name_lower_case'
          | 'name_normalized'
          | 'organization_id'
          | 'updated_at'
        > | null
      })[]
    | null
}

export type GroupedPayouts = {
  plan: number
  compensation_rule_id: number
  sum: number
  payouts: PayoutWithJoins[]
}

export type CompensationRuleWithJoins = Tables<'compensation_rules'> & {
  versions: Tables<'compensation_rule_versions'>[]
  label: string
  payouts: PayoutWithJoins[]
  deals: DealWithJoins[] | undefined
  custom_records?:
    | (CustomRecordsWithTables & {
        payouts?: PayoutWithJoins[]
        metric_values?: unknown
      })[]
    | null
  total_payouts_count: number
  total_payouts_value: number
  total_payouts_paid_value: number
  total_payouts_locked_value: number
  total_payouts_validated_count: number
}

export type FrozenColumn = {
  column: string
  description: string
  author: string
  created_at: string
}

export type ProfileWithJoins = Profile & {
  organizations_profiles: (Tables<'organizations_profiles'> & {
    get_profile_status_field: Enums<'profile_status'>
  })[]
  profile_org_variable_salary: Tables<'profile_org_variable_salary'>[] | null
  profile_org_start_date: Tables<'profile_org_start_date'>[] | null
  teams:
    | (Tables<'teams'> & {
        plans:
          | (Tables<'plans'>[] & {
              targets: (Tables<'targets'>[] & (Tables<'target_profile_amount'>[] | null)) | null
            })
          | null
        team_profiles: Tables<'teams_profiles'>[] | null
      })[]
    | null
  teams_profiles: (Tables<'teams_profiles'> & { teams: Tables<'teams'> | null })[] | null
  payouts: Tables<'payouts'>[] | null
  statements: Tables<'statements'>[] | null
  deals: DealWithJoins[] | null
}
export type ProfileWithOrganizationProfileInfoAndTeams = Tables<'profiles'> & {
  organizations_profiles: Tables<'organizations_profiles'>[]
  teams: Tables<'teams'>[] | null
}

export type TeamMember = Tables<'profiles'> & {
  organizations_profiles: Tables<'organizations_profiles'> &
    { get_profile_status_field: Enums<'profile_status'> }[]
} & {
  invitations: Tables<'invitations'>
} & Pick<Tables<'teams_profiles'>, 'role' | 'full_view_access' | 'team_id'>

export type TeamWithMembers = Tables<'teams'> & {
  teams_profiles: (Tables<'teams_profiles'> & {
    profiles: TeamMember
  })[]
}

export type TeamProfile = Database['public']['Tables']['teams_profiles']['Row']
export type TeamProfileInsert = Database['public']['Tables']['teams_profiles']['Insert']
export type ProfileInsert = Database['public']['Tables']['profiles']['Insert']
export type ProfileUpdate = Database['public']['Tables']['profiles']['Update']
export type EmailLeadInsert = Database['public']['Tables']['email-leads']['Insert']
export type TeamInsert = Database['public']['Tables']['teams']['Insert']
export type Template = Database['public']['Tables']['template']['Row']
export type TemplateWithJoins = Template & {
  tags: Tables<'tags'>[] | null
}
export type TemplateInsert = Database['public']['Tables']['template']['Insert']
export type Invitation = Database['public']['Tables']['invitations']['Row']
export type InvitationInsert = Database['public']['Tables']['invitations']['Insert']
export type OrganizationsProfiles = Database['public']['Tables']['organizations_profiles']['Row']
export type OrganizationsProfilesInsert =
  Database['public']['Tables']['organizations_profiles']['Insert']
export type Tags = Database['public']['Tables']['tags']['Row']
export type TagsInsert = Database['public']['Tables']['tags']['Insert']
export type Target = Database['public']['Tables']['targets']['Row']
export type Metric = Database['public']['Tables']['metrics']['Row']
export type TargetWithJoins = Tables<'targets'> & {
  plans: Tables<'plans'>[] | null
} & {
  target_amount: Tables<'target_profile_amount'>[] | null
  target_achievement: Tables<'target_achievement'>[] | null
  target_period_amount: Tables<'target_period_amount'>[]
}
export type TargetsInsert = Database['public']['Tables']['targets']['Insert']
export type DealWithJoins = Database['public']['Tables']['deals']['Row'] & {
  profiles: Tables<'profiles'> | null
  split_anchor?: Tables<'deals'> | null
  split_children?: Tables<'deals'>[] | null
  payouts: PayoutWithJoins[] | null
  data_issues_deals: Tables<'data_issues_deals'>[] | null
  activities: (Tables<'activities'> & { payouts: PayoutWithJoins[] })[] | null
  invoices: InvoiceWithJoins[]
  deal_line_items?: (Tables<'deal_line_items'> & { payouts?: PayoutWithJoins[] | null })[] | null
  kpis: Tables<'kpis'>[] | null
}
export type Deals = Omit<Tables<'deals'>, 'deals_search_columns'>

export type Execution = Database['public']['Tables']['executions']['Row']

export type Payout = Database['public']['Tables']['payouts']['Row']
export type PayoutWithJoins = Tables<'payouts'> & {
  statements: Tables<'statements'> | null
  executions:
    | (Tables<'executions'> & {
        compensation_rule_versions: Tables<'compensation_rule_versions'> | null
      })
    | null
  payout_installments:
    | (Tables<'payout_installments'> & {
        statements: Pick<Tables<'statements'>, 'id' | 'period_start' | 'status'> | null
      })[]
    | null
  current_payout_installment?: Tables<'payout_installments'> & {
    statements?: Pick<Tables<'statements'>, 'id' | 'period_start' | 'status'> | null
  }
  clawbacks: Tables<'clawbacks'> | null
  issues: Tables<'issues'>[] | null
  deals: DealWithJoins | null
  line_items: LineItemsWithJoins | null
  deal_line_items?: DealLineItemsWithJoins | null
  invoices: InvoiceWithJoins | null
  activities: ActivitiesWithDeals | null
  custom_records: CustomRecordsWithTables | null
  payout_categories: Tables<'payout_categories'> | null
  profiles:
    | (Tables<'profiles'> & {
        teams_profiles: (Tables<'teams_profiles'> & { teams: Tables<'teams'> | null })[] | null
      })
    | null
  plans: Tables<'plans'> | null
  compensation_rules:
    | (Tables<'compensation_rules'> & {
        kpis_tables: Tables<'kpis_tables'> | null
      })
    | null
  targets: Tables<'targets'> | null
  challenges: Tables<'challenges'> | null
  kpis: KpisWithTables | null
  payout_validations: (Tables<'payout_validations'> & { profiles: Tables<'profiles'> })[] | null
}

export type StatementsDataType = Tables<'statements'> & {
  issues?: Tables<'issues'>[] | null
  payouts: { sum: number }[]
  payout_installments: { sum: number }[]
  p_count?: { count: number }[]
  pi_count?: { count: number }[]
  profiles?: Tables<'profiles'> | null
  statement_approvals: (Tables<'payout_validations'> & { profiles: Tables<'profiles'> })[] | null
}

export type StatementsDetailsType = Tables<'statements'> & {
  total?: { sum: number }[]
  installments_total?: { sum: number }[]
  p_total: { sum: number }[]
  pi_total: { sum: number }[]
  p_count?: { count: number }[]
  pi_count?: { count: number }[]
  issues: Tables<'issues'>[]
  payout_installments: (Tables<'payout_installments'> & {
    payouts: PayoutWithJoins & { sum: number }
  })[]
  payouts: (PayoutWithJoins & { sum: number })[]
  profiles:
    | (Tables<'profiles'> & {
        teams_profiles: Pick<Tables<'teams_profiles'>, 'team_id'>[]
      })
    | null
}

export type HistoricStatementsType = Tables<'statements'> & {
  payouts: { sum: number }[]
  payout_installments: { sum: number }[]
}

export type MetricsWithJoins = Tables<'metrics'> & {
  kpis_tables: Tables<'kpis_tables'> | null
  custom_record_tables: Tables<'custom_record_tables'> | null
  plans:
    | (Tables<'plans'> & {
        kpis_tables: Pick<Tables<'kpis_tables'>, 'id' | 'label'>
      })
    | null
  targets: Tables<'targets'> | null
}

type TargetWithMetrics = Tables<'targets'> & {
  metrics: Tables<'metrics'> | null
  credit_setups: Pick<Tables<'credit_setups'>, 'name'> | null
}

export type TargetsWithMetricsAndTeams = TargetWithMetrics & {
  teams: (Tables<'teams'> & {
    teams_profiles: Tables<'teams_profiles'>[]
  })[]
}

export type TargetsWithMetricsAndTeamsList = TargetWithMetrics & {
  teams: Tables<'teams'>[]
}

export type PayoutInsert = Database['public']['Tables']['payouts']['Insert']

export type Invoices = Omit<Tables<'invoices'>, 'invoices_search_columns'>

export type InvoicesWithDeals = Invoices & {
  deals: Omit<Tables<'deals'>, 'deals_search_columns'> | null
}

export type InvoicesWithInnerDeals = Tables<'invoices'> & {
  deals: Tables<'deals'>
}

export type LineItemsWithJoins = Tables<'line_items'> & {
  invoices:
    | (Tables<'invoices'> & {
        deals: Tables<'deals'> | null
      })
    | null
}

export type DealLineItemsWithJoins = Tables<'deal_line_items'> & {
  deals: Deals | null
}

export type InvoiceWithJoins = Tables<'invoices'> & {
  deals: (Tables<'deals'> & { profiles: Tables<'profiles'> | null }) | null
  payouts: PayoutWithJoins[] | null
  line_items: (Tables<'line_items'> & { payouts?: PayoutWithJoins[] | null })[] | null
}

export type DealDrawerItemTypes =
  | (Tables<'activities'> & { payouts: PayoutWithJoins[] })
  | InvoiceWithJoins
  | (Tables<'deal_line_items'> & { payouts: PayoutWithJoins[] })

export type KpisWithTables = Tables<'kpis'> & {
  kpis_tables: Tables<'kpis_tables'> | null
}

export type KpisWithInnerTables = Tables<'kpis'> & {
  kpis_tables: Tables<'kpis_tables'>
}

export type TargetAchievementInsert = Database['public']['Tables']['target_achievement']['Insert']
export type CustomRecordsWithTables = Tables<'custom_records'> & {
  custom_record_tables: Tables<'custom_record_tables'> | null
}

export type ProfileOrgVariableSalary =
  Database['public']['Tables']['profile_org_variable_salary']['Row']
export type ProfileOrgVariableSalaryInsert =
  Database['public']['Tables']['profile_org_variable_salary']['Insert']

export type Activities = Omit<Tables<'activities'>, 'activities_search_columns'>

export type ActivitiesWithDeals = Activities & {
  deals: Omit<Tables<'deals'>, 'deals_search_columns'> | null
}

export type ActivitiesWithInnerDeals = Tables<'activities'> & {
  deals: Tables<'deals'>
}

export type ActivitiesWithProfiles = Tables<'activities'> & {
  profiles: ProfileWithOrganizationProfileInfoAndTeams | null
}

export type CompRuleRow = Tables<'compensation_rules'> & {
  kpis_tables?: Tables<'kpis_tables'> | null
  custom_record_tables?: Tables<'custom_record_tables'> | null
  compensation_rule_drafts?: Tables<'compensation_rule_drafts'> | null
  compensation_rule_versions?: Array<CompRuleVersionRow> | null
}

export type CompRuleVersionRow = Tables<'compensation_rule_versions'> & {
  compensation_rule_version_metrics?:
    | (Tables<'compensation_rule_version_metrics'> & { metrics: Tables<'metrics'> })[]
    | null
  compensation_rule_version_editors?:
    | (Tables<'compensation_rule_version_editors'> & { profiles: Tables<'profiles'> })[]
    | null
}

export type PlanWithJoins = Tables<'plans'> & {
  teams:
    | (Tables<'teams'> & {
        profiles: Tables<'profiles'>[] | null
      })[]
    | null
  compensation_letters?: Tables<'compensation_letters'>[] | null
  targets?: Tables<'targets'>[] | null
  compensation_rules?: CompRuleRow[] | null
}

export type CompRuleWithPlanTeamsAndDrafts = Tables<'compensation_rules'> & {
  plans: Tables<'plans'> & {
    teams: (Tables<'teams'> & { profiles: Tables<'profiles'> })[]
  }
  compensation_rule_drafts?: Tables<'compensation_rule_drafts'>
  compensation_rule_versions?: Tables<'compensation_rule_versions'>[]
}

export type DefinedPartial<T> = {
  [P in keyof T]?: Exclude<T[P], undefined>
}

export type TeamWithProfiles = Tables<'teams'> & {
  profiles: ProfileWithJoins[]
}

export type DBFunctions = Database['public']['Functions']

export type NotificationInsert = Database['public']['Tables']['notifications']['Insert']

export interface BadgeWonNotificationPayload {
  awardedAt: string
  badgeConfig: unknown
  badgeId: number
  badgeName: string
  issuedBadgeId: string
  source: 'challenge' | 'manual'
  sourceLabel: string
  winnerDisplayName: string
}

export type NotificationProfile = Omit<Tables<'profiles'>, 'invitations' | 'last_signed_in_at'>

export type CommentMentionWithJoins = Tables<'comments_mentions'> & {
  profiles: NotificationProfile | null
}

export type CommentWithJoins = Tables<'comments'> & {
  profiles: NotificationProfile | null
  comments_mentions: CommentMentionWithJoins[]
}

export type NotificationWithJoins = Tables<'notifications'> & {
  profiles: NotificationProfile | null
  notifications_status: Tables<'notifications_status'>[] | null
  comments: CommentWithJoins[] | null
}

export type NotificationsQuery = Tables<'notifications'> & {
  notifications_status: Tables<'notifications_status'>[]
  profiles: NotificationProfile
  comments: CommentWithJoins[]
}

export enum TABS {
  HOME = 'HOME',
  DASHBOARD = 'DASHBOARD',
  MY_TEAM = 'MY_TEAM',
  SUBSCRIPTIONS = 'SUBSCRIPTIONS',
  DEALS = 'DEALS',
  DATA = 'DATA',
  USERS = 'USERS',
  USER = 'USER',
  TEAMS = 'TEAMS',
  TEAM = 'TEAM',
  PLANS = 'PLANS',
  COMPENSATION_FRAMEWORK = 'COMPENSATION_FRAMEWORK',
  COMPENSATIONS = 'COMPENSATIONS',
  CHALLENGES = 'CHALLENGES',
  STATEMENT = 'STATEMENT',
  STATEMENTS = 'STATEMENTS',
  ISSUES = 'ISSUES',
  ACTIVITIES = 'ACTIVITIES',
  INVOICES = 'INVOICES',
  INTEGRATIONS = 'INTEGRATIONS',
  SETTINGS = 'SETTINGS',
  TARGETS = 'TARGETS',
  MOTIVATIONAL_FEATURES = 'MOTIVATIONAL_FEATURES',
  APPROVAL_FLOW = 'APPROVAL_FLOW',
  METRICS = 'METRICS',
  TARGET_ACHIEVEMENTS = 'TARGET_ACHIEVEMENTS',
  BADGE_CREATOR = 'BADGE_CREATOR'
}

export type Timeframe = { from?: Date; to?: Date; selectValue?: string }

export type GetPayoutsDataReturn =
  Database['public']['Functions']['get_payouts_data_with_change']['Returns']

export type SupabaseTableHookQueryOperators = {
  range?: {
    from?: number
    to?: number
  }
  rangeLt?: {
    column: string
    value: string
  }
  rangeGt?: {
    column: string
    value: string
  }
  overlaps?: {
    column: string
    value: string
  }
  ilikeFilters?: Array<{
    column: string
    value: string
  }>
  inFilters?: {
    column: string
    values: (string | number)[]
  }[]
  containsFilters?: {
    column: string
    values: (string | number)[]
  }[]
  filters?:
    | {
        column: string
        value: string | number | boolean | null
        operator: string
      }
    | {
        column: string
        value: string | number | boolean | null
        operator: string
      }[]
  customFieldFilters?: Array<
    | {
        sourceRole: 'local' | 'parent_deal' | 'parent_invoice'
        customFieldId: number
        operator: 'contains' | 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte'
        value: string | number | boolean
      }
    | {
        sourceRole: 'local' | 'parent_deal' | 'parent_invoice'
        customFieldId: number
        operator: 'includes' | 'excludes'
        values: string[]
      }
    | {
        sourceRole: 'local' | 'parent_deal' | 'parent_invoice'
        customFieldId: number
        operator: 'date_between'
        start: string
        end: string
      }
  >
  orFilter?:
    | string
    | [
        string,
        {
          foreignTable: string
        }
      ]
  // Multiple OR filters - each becomes a separate .or() call, which are ANDed together by PostgREST
  orFilters?: Array<
    | string
    | [
        string,
        {
          foreignTable: string
        }
      ]
  >
  eqFilters?: Array<{
    column: string
    value: string | number | boolean
  }>
  neqFilters?: Array<{
    column: string
    value: string | number | boolean | null
  }>
  notFilter?: [string, string, string | string[]]
  notFilters?: Array<[string, string, string | string[]]>
  gtFilters?: Array<{
    column: string
    value: string | number
    orEqual: boolean
  }>
  ltFilters?: Array<{
    column: string
    value: string | number
    orEqual: boolean
  }>
  order?: Array<{
    column: string
    options?: {
      ascending?: boolean
      nullsFirst?: boolean
      referencedTable?: string
    }
  }>
  isFilters?: Array<{
    column: string
    value: boolean | null
    negate?: boolean
  }>
  textSearch?: {
    column: string
    query: string
    options: {
      config?: string | undefined
      type?: 'plain' | 'phrase' | 'websearch' | undefined
    }
  }
  limit?: number
  timeRange?: Timeframe
}

export type SupabaseTableHook<T> = {
  enabled?: boolean
  queryKeys?: unknown[]
  onSuccess?: (items: { items: T[]; count: number | null }) => void
  onError?: (error: Error) => void
  setLoading?: (b: boolean) => void
  afterQuery?: (items: { items: T[]; count: number | null }) => void
  afterInsert?: (items: T[]) => void | Promise<void>
  afterUpsert?: (items: T[]) => void | Promise<void>
  afterUpdate?: (item: T) => void | Promise<void>
  afterUpdateItems?: (items: T[]) => void | Promise<void>
  afterGetById?: (item: T) => void | Promise<void>
  afterDelete?: (
    id: (number | string | T) | (string | number | T)[] | { [key: string]: unknown }
  ) => void | Promise<void>
  countItems?: boolean
  countType?: 'exact' | 'planned' | 'estimated'
  head?: boolean
  notify?: boolean
  notifyError?: boolean
  upsertOptions?: {
    onConflict?: string
    ignoreDuplicates?: boolean
    count?: 'exact' | 'planned' | 'estimated'
  }
  skipOrgFilter?: boolean
  refetchOnWindowFocus?: boolean
  refetchOnMount?: boolean
} & SupabaseTableHookQueryOperators

export type TemplateVariable = {
  type: 'string' | 'number'
  label: string
}

type JsonOrNull = Json | null

export type MapJsonToRecord<T> = {
  [P in keyof T]: T[P] extends JsonOrNull
    ? Record<string, unknown> | null
    : T[P] extends Json
      ? Record<string, unknown>
      : T[P]
}

export type MapToOptional<T> = {
  [P in keyof T]?: T[P]
}

export type DBFunctionReturns<FunctionName extends keyof Database['public']['Functions']> =
  FunctionName extends keyof Database['public']['Functions']
    ? Database['public']['Functions'][FunctionName]['Returns']
    : never

export type Tag = {
  id?: string
  type: string
  label: MessageDescriptor
  color?: string
  parsedValue?: string | number
  definition: MessageDescriptor
  matcher: { test: (text: string) => boolean }
  options?:
    | Record<
        string,
        {
          label: MessageDescriptor
          description: MessageDescriptor
        }
      >
    | string[]
  source?: Enums<'plan_source'>
}

export type GridTable = keyof Pick<
  Database['public']['Tables'],
  | 'deals'
  | 'kpis'
  | 'custom_records'
  | 'profiles'
  | 'invoices'
  | 'activities'
  | 'metrics'
  | 'targets'
  | 'statements'
  | 'payouts'
  | 'data_issues_invoices'
  | 'data_issues_deals'
  | 'line_items'
  | 'deal_line_items'
>

export type DatabaseTableName = keyof Database['public']['Tables']

export enum TargetAchievementStatuses {
  UPCOMING = 'upcoming',
  ONGOING_FOLLOW_PACE = 'ongoing-follow-pace',
  ONGOING_NOT_FOLLOW_PACE = 'ongoing-not-follow-pace',
  ONGOING_ACHIEVED = 'ongoing-achieved',
  ONGOING_FIRST_WEEK = 'ongoing-first-week',
  FINISHED_ACHIEVED = 'finished-achieved',
  FINISHED_NOT_ACHIEVED = 'finished-not-achieved'
}

export const payoutLogEditionFields = [
  'description',
  'type',
  'period_start',
  'period_end',
  'statement_id'
]

export type Nullable<T> = {
  [K in keyof T]: T[K] | null
}

export type DataIssuesWithChildren = Tables<'data_issues'> &
  Partial<Tables<'data_issues_deals'>> &
  Partial<Tables<'data_issues_invoices'>>

export type DataIssuesWithChildrenInsert = TablesInsert<'data_issues'> &
  Partial<Nullable<Tables<'data_issues_deals'>>> &
  Partial<Nullable<Tables<'data_issues_invoices'>>>
